import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Landmark, ChevronLeft, Calculator, ShieldCheck } from "lucide-react";
import DebtNavbar from "../components/layout/DebtNavbar";
import Footer from "../components/layout/Footer";
import Seo from "../components/Seo";
import api from "../lib/api";

const fmtINR = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n % 1e7 === 0 ? 0 : 2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} L`;
  return `₹${(n / 1000).toFixed(0)} K`;
};
const fmtRup = (n) => n?.toLocaleString("en-IN", { maximumFractionDigits: 0 }) || "0";

/** EMI formula: P·r·(1+r)^n / ((1+r)^n − 1) */
function emi(principal, ratePct, months) {
  const r = ratePct / 12 / 100;
  if (!r) return principal / months;
  const pow = Math.pow(1 + r, months);
  return (principal * r * pow) / (pow - 1);
}

export default function DebtProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  const [amount, setAmount] = useState(1500000);
  const [tenure, setTenure] = useState(60);
  const [rate, setRate] = useState(11);

  useEffect(() => {
    setLoading(true);
    api.get(`/public/products/${slug}`).then((r) => {
      setP(r.data);
      // Sensible defaults based on the product's range.
      const min = r.data?.min_amount || 100000;
      const max = r.data?.max_amount || 10000000;
      setAmount(Math.round((min + Math.min(max, min * 5)) / 2));
      setTenure(Math.min(60, r.data?.tenure_max || 60));
      setRate(r.data?.rate_from || 11);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const monthly = useMemo(() => emi(amount, rate, tenure), [amount, rate, tenure]);
  const totalInterest = useMemo(() => monthly * tenure - amount, [monthly, tenure, amount]);

  if (loading) return (
    <div className="min-h-screen bg-cream"><DebtNavbar /><div className="max-w-4xl mx-auto p-10 text-sm text-ink-muted">Loading…</div></div>
  );
  if (!p) return (
    <div className="min-h-screen bg-cream"><DebtNavbar /><div className="max-w-4xl mx-auto p-10">
      <div className="text-sm text-ink-muted mb-2">Product not found.</div>
      <Link to="/venturaz-money/products" className="text-[#7A4E1D] underline">← All products</Link>
    </div></div>
  );

  return (
    <div className="min-h-screen text-ink bg-cream" data-testid="debt-product-detail">
      <Seo title={`${p.title} · VenturaZ MONEY`} description={`${p.tagline}. Rates from ${p.rate_from}% p.a. Compare offers from 85+ lenders.`} path={`/venturaz-money/product/${p.slug}`} />
      <DebtNavbar />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #1F1408 0%, #3E2410 40%, #7A4E1D 100%)" }}
        data-testid="debt-product-hero"
      >
        <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-30" style={{ background: "radial-gradient(circle, #E9C46A 0%, transparent 60%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 py-12 lg:py-16 text-[#FFF3D9]">
          <Link to="/venturaz-money/products" className="inline-flex items-center gap-1 text-xs text-[#E9C46A] hover:underline mb-4" data-testid="debt-product-back">
            <ChevronLeft size={12} /> All 15 products
          </Link>
          <div className="inline-flex items-center gap-1.5 bg-gold text-[#1F1408] text-[10px] font-mono uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full mb-4">
            From {p.rate_from}% p.a. · Up to {fmtINR(p.max_amount)}
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight max-w-3xl mb-3" data-testid="debt-product-title">{p.title}</h1>
          <p className="text-[#FFF3D9]/80 text-lg leading-relaxed max-w-2xl mb-8">{p.tagline}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to={`/money?purpose=${encodeURIComponent(p.key || "")}&audience=${encodeURIComponent(p.audience || "individual")}&product=${p.slug}#lead-form`} className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-gold text-[#1F1408] text-sm font-heading font-bold hover:bg-yellow-400 transition-colors shadow-[0_10px_30px_rgba(233,196,106,0.35)]" data-testid="debt-product-apply">
              Apply for this loan <ArrowRight size={15} />
            </Link>
            <Link to="/venturaz-money/banks" className="inline-flex items-center gap-1 text-sm text-[#E9C46A] hover:underline" data-testid="debt-product-lenders-link">
              See our lender panel →
            </Link>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* At a glance */}
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-2xl border border-[#B8860B]/25 bg-white p-6" data-testid="debt-product-glance">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-3">At a glance</div>
            <Row k="Rates from" v={`${p.rate_from}% p.a.`} />
            <Row k="Tenure up to" v={`${p.tenure_max} months`} />
            <Row k="Min amount" v={fmtINR(p.min_amount)} />
            <Row k="Max amount" v={fmtINR(p.max_amount)} />
            <Row k="Turnaround" v="48–96 hrs (indicative)" />
            <Row k="Platform fee" v="₹0 (advisor-model)" />
          </div>

          <div className="rounded-2xl border border-[#B8860B]/25 bg-white p-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-3 inline-flex items-center gap-1">
              <ShieldCheck size={12} /> What we do differently
            </div>
            <ul className="text-[13px] text-ink-soft space-y-2 leading-relaxed">
              <li>• One credit specialist owns your file end-to-end — no IVR mazes.</li>
              <li>• Free CIBIL pull worth ₹999 to spot anomalies before applying.</li>
              <li>• We only earn when the loan disburses — so we don&apos;t chase weak leads.</li>
              <li>• Native e-sign on mandate at zero fee.</li>
            </ul>
          </div>
        </div>

        {/* EMI calculator */}
        <div className="lg:col-span-2 rounded-2xl border border-[#B8860B]/25 bg-white p-6" data-testid="debt-product-emi">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-3 inline-flex items-center gap-1">
            <Calculator size={12} /> {p.title} EMI Calculator
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <SliderField label="Loan amount" min={p.min_amount} max={p.max_amount} step={Math.max(50000, Math.round((p.max_amount - p.min_amount) / 200))} value={amount} setValue={setAmount} format={fmtINR} testId="emi-amount" />
            <SliderField label="Tenure (months)" min={6} max={p.tenure_max} step={6} value={tenure} setValue={setTenure} format={(v) => `${v} m`} testId="emi-tenure" />
            <SliderField label="Rate (% p.a.)" min={p.rate_from} max={p.rate_from + 6} step={0.1} value={rate} setValue={setRate} format={(v) => `${v.toFixed(2)}%`} testId="emi-rate" />
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <ResultTile label="Monthly EMI" value={`₹${fmtRup(monthly)}`} highlight />
            <ResultTile label="Total interest" value={`₹${fmtRup(totalInterest)}`} />
            <ResultTile label="Total payable" value={`₹${fmtRup(monthly * tenure)}`} />
          </div>
          <div className="mt-6 flex items-center gap-2 text-[11px] text-ink-muted">
            <Landmark size={12} className="text-[#7A4E1D]" /> Indicative only. Actual EMI depends on the lender, product policy, and your credit profile.
          </div>
        </div>
      </section>

      {/* Sample lender panel */}
      <section className="max-w-6xl mx-auto px-6 pb-14" data-testid="debt-product-lender-panel">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-2">Sample lender panel</div>
        <h2 className="font-display text-2xl leading-tight mb-6">A slice of the lenders we tap for {p.title.toLowerCase()}s</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(p.lenders || []).map((l) => (
            <div key={l.name} className="border border-[#B8860B]/20 rounded-xl p-4 bg-white" data-testid={`debt-product-lender-${l.name.replace(/\s+/g, "-")}`}>
              <div className="font-heading text-ink text-sm mb-1">{l.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-2">{l.lender_type}</div>
              <div className="text-[11px] text-ink-soft">ROI {l.roi_min}–{l.roi_max}% · TAT {l.tat_days}d</div>
            </div>
          ))}
        </div>
        <div className="mt-5 text-[11px] text-ink-muted">
          Full 114-lender directory · <Link to="/venturaz-money/banks" className="text-[#7A4E1D] hover:underline">See all →</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-ink/[0.05] last:border-0">
      <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">{k}</div>
      <div className="text-sm font-heading text-ink">{v}</div>
    </div>
  );
}

function SliderField({ label, min, max, step, value, setValue, format, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
        <div className="text-sm font-heading text-[#7A4E1D]" data-testid={`${testId}-value`}>{format(value)}</div>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => setValue(Number(e.target.value))} className="w-full accent-[#7A4E1D]" data-testid={testId} />
    </div>
  );
}

function ResultTile({ label, value, highlight }) {
  return (
    <div className={`rounded-lg p-4 ${highlight ? "bg-[#7A4E1D] text-[#FFF3D9]" : "bg-[#FBFAF5] border border-[#B8860B]/20 text-ink"}`}>
      <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${highlight ? "text-[#FFF3D9]/70" : "text-ink-muted"}`}>{label}</div>
      <div className="font-display text-xl leading-tight">{value}</div>
    </div>
  );
}
